import React, { useState } from "react";
import styles from "./OrderBoxV2.module.scss";
import { ReactComponent as RFZOIcon } from "../../../assets/icons/rfzo-icon.svg";
import { useDispatch } from "react-redux";
import { orderActions } from "../../../store/order-slice";
import { rfzoTherapyActions } from "../../../store/rfzoTherapy-slice";
import moment from "moment";
import Button from "../../UI/Buttons/Button/Button";
import ProgressBar from "../../ProgressBar/ProgressBar";
import OrderDetails from "../OrderDetails/OrderDetails";
import RFZOOrderDetails from "../../RFZOOrderDetails/RFZOOrderDetails";
import classNames from "classnames";
import ArrowUpIcon from "@rsuite/icons/ArrowUp";
import {
  CnCOrderResponse,
  CnCTransactionResponse,
  RFZOTherapyResponse,
  isCanceledByUser,
} from "../../../types/api";
import { AppBrandResponse } from "../../../types/shared";
import {
  isFastDelivery,
  showsDeliveryVanIcon,
} from "../../../utils/deliveryType";

type Props = {
  order?: CnCOrderResponse | CnCTransactionResponse;
  rfzoRequest?: RFZOTherapyResponse;
  isAdmin?: boolean;
  isDriver?: boolean;
  archived?: boolean;
  processed?: boolean;
};

// App Brand Display Component
interface AppBrandDisplayProps {
  appBrand?: AppBrandResponse | null;
  className?: string;
}

const AppBrandDisplay: React.FC<AppBrandDisplayProps> = ({
  appBrand,
  className = "",
}) => {
  const [imageError, setImageError] = useState(false);

  // Return null if no app brand data or neither name nor logo exists
  if (!appBrand || (!appBrand.name && !appBrand.logo)) {
    return null;
  }

  // Construct CDN URL if logo exists
  const logoUrl = appBrand.logo
    ? `${process.env.REACT_APP_CDN_PATH}${appBrand.logo}`
    : null;

  return (
    <div className={`${styles.appBrand} ${className}`}>
      {logoUrl && !imageError && (
        <img
          src={logoUrl}
          alt={appBrand.name ? `${appBrand.name} logo` : "App brand logo"}
          className={styles.appBrandLogoImage}
          onError={() => setImageError(true)}
          onLoad={() => setImageError(false)}
          loading="eager"
        />
      )}
      {appBrand.name && (
        <span className={styles.appBrandValue}>{appBrand.name}</span>
      )}
    </div>
  );
};

const SUCCESS_STATUSES = ["TAKEN", "IN_DELIVERY"];

const OrderBox = ({
  order,
  rfzoRequest,
  isAdmin,
  processed,
  isDriver,
  archived,
}: Props) => {
  let orderBoxData: any;

  if (rfzoRequest) {
    orderBoxData = {
      ...rfzoRequest,
      id: rfzoRequest.id,
      date: new Date("" + rfzoRequest.created),
      firstName: rfzoRequest.firstName,
      secondName: rfzoRequest.secondName,
      phoneNumber: rfzoRequest.userPhone,
      status: rfzoRequest.status,
      userLBO: rfzoRequest.userLBO,
      deliveryPrice: 0, // RFZO requests don't have delivery price
      totalPriceWithDelivery: rfzoRequest.totalPrice,
      isRFZO: true,
      processed: rfzoRequest.modified
        ? new Date("" + rfzoRequest.modified)
        : undefined,
    };
  } else if (order) {
    // Helper function to determine if this is a transaction
    const isTransaction = (
      order: CnCOrderResponse | CnCTransactionResponse,
    ): order is CnCTransactionResponse => {
      return "orderNumber" in order && "userPhoneNumber" in order;
    };

    // Extract phone number based on order type
    const getPhoneNumber = (
      order: CnCOrderResponse | CnCTransactionResponse,
    ): string => {
      if (isTransaction(order)) {
        return order.userPhoneNumber;
      } else {
        return order.userPhoneNumberForOrder;
      }
    };

    // Extract processed date based on order type
    const getProcessedDate = (
      order: CnCOrderResponse | CnCTransactionResponse,
    ): Date | undefined => {
      if (isTransaction(order)) {
        return order.orderProcessed
          ? new Date(order.orderProcessed)
          : undefined;
      } else {
        return order.processed ? new Date(order.processed) : undefined;
      }
    };

    // Extract creation date based on order type
    const getCreationDate = (
      order: CnCOrderResponse | CnCTransactionResponse,
    ): Date => {
      if (isTransaction(order)) {
        return new Date(order.orderCreated || order.created);
      } else {
        return new Date(order.created);
      }
    };

    // Get the correct display ID based on context
    const getDisplayId = (
      order: CnCOrderResponse | CnCTransactionResponse,
    ): number => {
      // For archived transactions, show the original order number
      if (archived && isTransaction(order)) {
        return order.orderNumber;
      }
      // For everything else (orders, non-archived transactions), show the ID
      return order.id;
    };

    orderBoxData = {
      ...order,
      id: getDisplayId(order),
      date: getCreationDate(order),
      firstName: order.firstName,
      secondName: order.secondName,
      phoneNumber: getPhoneNumber(order),
      items: order.items,
      noteForUser: order.noteForUser,
      rfzoTherapyNumber: isTransaction(order)
        ? undefined
        : order.rfzoTherapyNumber,
      status: order.status,
      totalPrice: order.totalPrice,
      deliveryPrice: order.deliveryPrice,
      totalPriceWithDelivery: order.totalPriceWithDelivery,
      processed: getProcessedDate(order),
      orderCreated: isTransaction(order)
        ? order.orderCreated
          ? new Date("" + order.orderCreated)
          : undefined
        : new Date("" + order.created),
      orderProcessed: isTransaction(order)
        ? order.orderProcessed
          ? new Date("" + order.orderProcessed)
          : undefined
        : order.processed
          ? new Date("" + order.processed)
          : undefined,
      created: new Date("" + order.created),
    };
  } else {
    // Fallback for empty state
    orderBoxData = {
      id: 0,
      date: new Date(),
      firstName: "",
      secondName: "",
      phoneNumber: "",
      status: "UNKNOWN",
      totalPrice: 0,
      deliveryPrice: 0,
      totalPriceWithDelivery: 0,
    };
  }

  // Helper function to get app brand data for display
  const getAppBrandForDisplay = (): AppBrandResponse | undefined => {
    if (rfzoRequest) {
      return rfzoRequest.userResponse?.appBrand;
    } else if (order) {
      return order.userResponse?.appBrand;
    }
    return undefined;
  };

  const dispatch = useDispatch();

  const showModal = () => {
    if (rfzoRequest) {
      setRfzoExpanded(true);
      dispatch(rfzoTherapyActions.toggleRFZORequestModal(orderBoxData));
    } else {
      dispatch(orderActions.toggleOrderModal(order));
      setExpanded(true);
    }
  };

  const [expanded, setExpanded] = useState(false);
  const [rfzoExpanded, setRfzoExpanded] = useState(false);

  const isSuccessful = SUCCESS_STATUSES.includes(orderBoxData.status);
  const deliveryBorderClass =
    !rfzoRequest && order
      ? isFastDelivery(order)
        ? styles.deliveryFast
        : showsDeliveryVanIcon(order)
          ? styles.deliveryStandard
          : undefined
      : undefined;

  const renderTimestamps = () => {
    if (!archived && !processed && !orderBoxData.processed) {
      return (
        <div className={styles.timestamps}>
          <div className={styles.timestampRow}>
            <span className={styles.timestampLabel}>Kreirana:</span>
            <span className={styles.timestampValue}>
              {moment(orderBoxData.date).format("HH:mm DD.MM.YYYY")}
            </span>
          </div>
        </div>
      );
    } else if (processed || (!archived && orderBoxData.processed)) {
      return (
        <div className={styles.timestamps}>
          <div className={styles.timestampRow}>
            <span className={styles.timestampLabel}>Kreirana:</span>
            <span className={styles.timestampValue}>
              {moment(orderBoxData.date).format("HH:mm DD.MM.YYYY")}
            </span>
          </div>
          {orderBoxData.processed && (
            <div className={styles.timestampRow}>
              <span className={styles.timestampLabel}>Prihvaćena:</span>
              <span className={styles.timestampValue}>
                {moment(orderBoxData.processed).format("HH:mm DD.MM.YYYY")}
              </span>
            </div>
          )}
        </div>
      );
    } else if (archived) {
      const finalStatusLabel = statusMapping[orderBoxData.status] || "Preuzeta";

      return (
        <div className={styles.timestamps}>
          <div className={styles.timestampRow}>
            <span className={styles.timestampLabel}>Kreirana:</span>
            <span className={styles.timestampValue}>
              {moment(orderBoxData.orderCreated || orderBoxData.date).format(
                "HH:mm DD.MM.YYYY",
              )}
            </span>
          </div>
          {(orderBoxData.processed || orderBoxData.orderProcessed) && (
            <div className={styles.timestampRow}>
              <span className={styles.timestampLabel}>Prihvaćena:</span>
              <span className={styles.timestampValue}>
                {moment(
                  orderBoxData.orderProcessed || orderBoxData.processed,
                ).format("HH:mm DD.MM.YYYY")}
              </span>
            </div>
          )}
          {orderBoxData.created && orderBoxData.status !== "PROCESSED" && (
            <div className={styles.timestampRow}>
              <span className={styles.timestampLabel}>{finalStatusLabel}:</span>
              <span className={styles.timestampValue}>
                {moment(orderBoxData.created).format("HH:mm DD.MM.YYYY")}
              </span>
            </div>
          )}
        </div>
      );
    }

    return null;
  };
  return (
    <div
      className={classNames(styles.orderBox, deliveryBorderClass, {
        [styles.success]: archived && isSuccessful,
        [styles.failed]: archived && !isSuccessful,
      })}
    >
      <div className={styles.cardHeader}>
        <div className={styles.dataAndId}>
          <span className={styles.orderId}>#{orderBoxData.id}</span>
        </div>
        <div className={styles.timerAndStatus}>
          {isAdmin && (
            <div className={styles.appBrandSection}>
              <AppBrandDisplay appBrand={getAppBrandForDisplay()} />
            </div>
          )}
          {archived ? (
            <div
              className={classNames(styles.orderStatus, {
                [styles.success]: true,
                [styles.failed]: true,
              })}
            >
              {statusMapping[orderBoxData?.status]}
            </div>
          ) : !processed ? (
            <ProgressBar
              intervalLength={10}
              intervalType={"m"}
              startPoint={orderBoxData.date}
            />
          ) : null}
        </div>
      </div>
      {expanded && isAdmin && (
        <div>
          Apoteka: {orderBoxData?.marketplaceResponse?.name},{" "}
          {orderBoxData?.marketplaceResponse?.location}
        </div>
      )}
      <div className={styles.cardBody}>
        <div className={styles.userName}>
          {orderBoxData?.firstName + " " + orderBoxData?.secondName}
          <br />
          {isAdmin && (
            <div style={{ fontWeight: "400", fontSize: "14px" }}>
              {orderBoxData?.marketplaceResponse?.name}
            </div>
          )}
        </div>
        <div className={styles.rfzoAndPrice}>
          {(orderBoxData.isRFZO || orderBoxData.rfzoTherapyNumber) && (
            <div>
              RFZO <RFZOIcon />
              <br />
              <div className={styles.porudzbinaLabel}>
                {!orderBoxData?.rfzoTherapyNumber ? "ZAHTEV" : "REZERVACIJA"}
              </div>
            </div>
          )}
          {archived &&
            (orderBoxData?.rfzoTherapyNumber
              ? ((order as CnCOrderResponse)?.totalPriceWithoutDiscount ||
                  orderBoxData?.totalPrice) + " RSD"
              : orderBoxData?.totalPrice + " RSD")}
        </div>
      </div>
      {isCanceledByUser(orderBoxData.status) && (
        <div className={styles.userCancellation}>
          <div className={styles.userCancellationTitle}>Otkazao korisnik</div>
          {orderBoxData.noteForCanceledStatus && (
            <div
              className={styles.userCancellationReason}
              title={orderBoxData.noteForCanceledStatus}
            >
              Razlog: {orderBoxData.noteForCanceledStatus}
            </div>
          )}
        </div>
      )}
      {renderTimestamps()}
      {!expanded && !rfzoExpanded && (
        <div className={styles.cardFooter}>
          <Button
            style={{ fontSize: "14px" }}
            onClick={() => {
              showModal();
            }}
            buttonType={
              (archived && isSuccessful) || !archived ? "blue" : "gray"
            }
          >
            {!processed && !archived && "Obradi"}
            {processed && "Evidentiraj"}
            {archived && "Otvori"}
          </Button>
        </div>
      )}{" "}
      {expanded && (
        <OrderDetails
          archived={archived}
          collapseOrder={() => setExpanded(false)}
          order={order as CnCOrderResponse} // Cast to CnCOrderResponse since OrderDetails only handles orders, not transactions
          isDriver={false}
          isAdmin={!!isAdmin}
        />
      )}
      {rfzoExpanded && (
        <RFZOOrderDetails
          archived={archived}
          rfzoRequest={orderBoxData}
          show={rfzoExpanded}
        />
      )}
      {(expanded || rfzoExpanded) && (
        <div
          style={{ textAlign: "center", color: "#a9a9a9", fontSize: "20px" }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (expanded || rfzoExpanded) {
              setExpanded(false);
              setRfzoExpanded(false);
              dispatch(orderActions.toggleOrderModal(null));
            }
          }}
        >
          <ArrowUpIcon height={32} width={32} />
        </div>
      )}
    </div>
  );
};

export default OrderBox;

interface Statuses {
  [key: string]: string;
}

const statusMapping: Statuses = {
  CREATED: "Kreirano",
  REQUESTED: "Zatrazeno",
  PREPARED: "Pripremljeno",
  PROCESSED: "Obradjeno",
  TAKEN: "Preuzeto",
  IN_DELIVERY: "Preuzeto",
  REJECTED: "Odbijeno",
  CANCELED: "Otkazano",
  CANCELED_BY_USER: "Otkazano",
  SUSPENDED: "Propušteno",
  REQUEST_EXPIRED: "Propušteno",
  PROCESSED_WITHOUT_CONTENT: "Odbijeno",
  REJECT_REQUESTED: "Odbijeno",
};
